<script setup lang="ts">
import { Loader2, ExternalLink, Plus, ChevronLeft } from 'lucide-vue-next'
import PartnerAuthForm from './components/PartnerAuthForm.vue'
import MarketplaceAccountList from './components/MarketplaceAccountList.vue'
import MarketplaceAccountDetails from './components/MarketplaceAccountDetails.vue'
import MarketplaceAccountConfiguration from './components/MarketplaceAccountConfiguration.vue'
import axios, { type CancelTokenSource } from 'axios'
import type { Partner } from '@/types/types'
import { type PartnerStore } from '@/types/types'
import type { TabKey } from '@/components/base/BaseTab.vue'

interface PartnerAccount {
  id: number
  publicId: string
  isActive: boolean
  qrCodeLoyalty?: boolean
}

interface AccountModel {
  isActive?: boolean
  apiUrl?: string
  apiUsername?: string
  apiToken?: string
  merchantIdFromPartner?: string
}

interface DropdownOption {
  id: number | null
  text: string
}

interface StoreAssociation {
  hasError: boolean
  partnerStoreId: number | null
  hash: string | null
  storeIdFromPartner: string | null
  storeNameFromPartner: string | null
  dropdownSelection: DropdownOption
}

const { t } = useI18n()
const { user, stores, isSupportConnection } = useLocalStorage()
const { openInNewTab, delay } = useDOM()
const { hasPermissionBySlug } = useAccess()
const notification = useNotification()
const { howToUrl, statusUrl } = useIntegration()

const isLoaded = ref(false)
const isModalOpen = ref(false)
const partners = ref<Partner[]>([])
const accounts = ref<PartnerAccount[]>([])
const selectedPartner = ref<Partner | null>(null)
const isAccountsLoading = ref<number | null>(null)
const isAuthStep = ref(false)
const accountModel = ref<AccountModel>({})
const isLoadingAccount = ref(false)
const isSyncingCustomers = ref(false)
const selectedAccountDetails = ref<PartnerAccount | null>(null)
const selectedAccountConfiguration = ref<PartnerAccount | null>(null)
const isLoadingConfiguration = ref(false)
const isLoadingConfigurationSubmit = ref(false)
const partnerStores = ref<PartnerStore[]>([])
const storeAssociations = ref<StoreAssociation[]>([])
const route = useRoute()
const router = useRouter()
let cancelGetStoresWithAccount: CancelTokenSource | undefined

const activeTab = ref('orders-payments')
const tabs = ref<TabKey[]>([
  {
    name: 'Commandes & paiements',
    value: 'orders-payments',
    icon: 'HeroCurrencyEuroIconOutline' as const
  },
  ...(isSupportConnection.value
    ? [
        {
          name: 'Emailing',
          value: 'emailing',
          icon: 'HeroEnvelopeIconOutline' as const
        }
      ]
    : [])
])

// Interface pour les partenaires emailing de l'API
interface EmailingPartner {
  name: string
  logo: string
  howToUrl: string
  findApiKeyUrl: string
  available: boolean
  slug: string
}

// Données pour les partenaires d'emailing
const emailingPartnersData = ref<EmailingPartner[]>([])
const isEmailingModalOpen = ref(false)
const selectedEmailingPartner = ref<EmailingPartner | null>(null)
const emailingIntegrations = ref<Array<{ client: string; active: boolean; id?: string }>>([])
const isTogglingIntegration = ref<string | null>(null)
const isLoadingEmailingIntegrations = ref(false)
const isLoadingEmailingPartners = ref(false)

// Données pour le formulaire d'intégration emailing
const emailingFormStep = ref(1) // 1: API Key, 2: Liste de contacts, 3: Succès
const emailingApiKey = ref('')
const isLoadingContactLists = ref(false)
const contactLists = ref<DropdownOption[]>([])
const selectedContactList = ref<DropdownOption | null>(null)
const isActivatingIntegration = ref(false)

// Étapes du formulaire emailing
const emailingSteps = [
  t('pages.marketplace.emailingPage.stepper.step1'),
  t('pages.marketplace.emailingPage.stepper.step2'),
  t('pages.marketplace.emailingPage.stepper.step3')
]

const getVisiblePartners = async () => {
  const { data } = await axios.get('partners', {
    params: {
      isVisibleInMarketplace: 1,
      finalRedirectUri: `/marketplace?partner={partner}`
    }
  })
  partners.value = data.data

  isLoaded.value = true
}

const getAccounts = async (): Promise<PartnerAccount[]> => {
  if (!selectedPartner.value) return []
  const { data } = await axios.get(`/partners/${selectedPartner.value.id}/accounts`, {
    params: {
      finalRedirectUri: '/marketplace?partner={partner}'
    }
  })
  return data.data
}

const configure = async (partner: Partner) => {
  selectedPartner.value = partner
  isAccountsLoading.value = partner.id
  accounts.value = await getAccounts()
  isModalOpen.value = true
  isAccountsLoading.value = null
}

watchEffect(async () => {
  if (partners.value.length) {
    const partner = partners.value.find((p) => p.id === Number(route.query.partner))
    if (partner) {
      await configure(partner)
      const queryWithoutPartner = { ...route.query }
      delete queryWithoutPartner.partner
      router.replace({ query: queryWithoutPartner })
    }
  }
})

const handleStatusUpdate = async (account: PartnerAccount) => {
  if (!selectedPartner.value) return
  account.isActive = !account.isActive
  const isActive = account.isActive
  await axios.patch(`/partners/${selectedPartner.value.id}/accounts/${account.id}`, {
    isActive
  })
  const partner = partners.value.find((p) => p.id === selectedPartner.value?.id)
  if (partner) {
    partner.accountCount += isActive ? 1 : -1
  }
}

const getStoresWithAccount = async () => {
  if (!selectedPartner.value || !selectedAccountConfiguration.value) return []
  if (cancelGetStoresWithAccount) {
    cancelGetStoresWithAccount.cancel()
  }

  cancelGetStoresWithAccount = axios.CancelToken.source()
  const { data } = await axios.get(`/partners/${selectedPartner.value.id}/stores`, {
    params: {
      accountId: selectedAccountConfiguration.value.id
    },
    cancelToken: cancelGetStoresWithAccount.token
  })
  return data.data
}

const handleSelectedAccountConfiguration = async (account: PartnerAccount | null) => {
  if (!account) return
  isLoadingConfiguration.value = true
  selectedAccountConfiguration.value = account
  partnerStores.value = await getStoresWithAccount()
  storeAssociations.value = partnerStores.value.map((p) => {
    const dropdownSelection: DropdownOption = {
      id: null,
      text: t('pages.loyalty.create.instructions.noStore')
    }
    if (p.storeId) {
      dropdownSelection.id = p.storeId
      dropdownSelection.text = p.storeName ?? ''
    }
    return {
      hasError: false,
      partnerStoreId: p.id,
      hash: p.hash,
      storeIdFromPartner: p.storeIdFromPartner,
      storeNameFromPartner: p.storeNameFromPartner,
      dropdownSelection
    }
  })
  isLoadingConfiguration.value = false
}

const handleCustomerSyncRequested = async (account: PartnerAccount) => {
  if (isSyncingCustomers.value || !selectedPartner.value) {
    return
  }
  try {
    const { data } = await axios.post(`/partners/${selectedPartner.value.id}/accounts/${account.id}/customers/sync`)
    const isSuccess = data.data.syncingCustomers
    notification.create(isSuccess ? 'success' : 'error', {
      text: t(`integrations.customerSyncing.notification.${isSuccess ? 'syncSuccess' : 'syncError'}`)
    })
    isSyncingCustomers.value = isSuccess
  } catch {
    notification.create('error', {
      text: t('integrations.customerSyncing.notification.syncError')
    })
  }
}

const checkValidity = () => {
  if (!selectedPartner.value) return false
  if (selectedPartner.value.hasCustomApiUrl && !accountModel.value?.apiUrl) {
    notification.create('error', {
      text: t('pages.marketplace.marketplacePage.accountModel.apiUrlError')
    })
    return false
  }
  const authType = selectedPartner.value.partnerAuthenticationType?.slug || ''
  if (authType === 'basic-auth' || authType === 'password') {
    if (!accountModel.value?.apiUsername) {
      notification.create('error', {
        text:
          authType === 'basic-auth'
            ? t('pages.marketplace.marketplacePage.accountModel.apiKeyError')
            : t('pages.marketplace.marketplacePage.accountModel.apiUsernameError')
      })
      return false
    }

    if (!accountModel.value?.apiToken) {
      notification.create('error', {
        text:
          authType === 'basic-auth'
            ? t('pages.marketplace.marketplacePage.accountModel.apiKeySecretError')
            : t('pages.marketplace.marketplacePage.accountModel.apiPasswordError')
      })
      return false
    }
  }
  if (authType === 'token' && !accountModel.value?.apiToken) {
    notification.create('error', {
      text: t('pages.marketplace.marketplacePage.accountModel.apiKeyError')
    })
    return false
  }
  if (selectedPartner.value.isMerchantAssociationRequired && !accountModel.value?.merchantIdFromPartner) {
    notification.create('error', {
      text: t('pages.marketplace.marketplacePage.accountModel.merchantIdFromPartnerError')
    })
    return false
  }
  return true
}

const addAccount = async () => {
  if (!selectedPartner.value) return
  const isValid = checkValidity()
  if (!isValid) {
    return
  }
  isLoadingAccount.value = true
  try {
    if (selectedPartner.value.partnerAuthenticationType?.slug === 'internal') {
      accountModel.value.isActive = true
    }
    const { data } = await axios.post(`/partners/${selectedPartner.value.id}/accounts`, {
      ...accountModel.value,
      merchantId: user.value.merchant.id
    })
    await delay(2000)
    accounts.value = await getAccounts()
    const findIndex = partners.value.findIndex((p) => p.id === selectedPartner.value?.id)
    if (findIndex !== -1) {
      partners.value[findIndex].accountCount++
    }
    isAuthStep.value = false

    if (selectedPartner.value.partnerAuthenticationType?.slug !== 'internal') {
      selectedAccountConfiguration.value = data.data
      await handleSelectedAccountConfiguration(data.data)
    } else {
      selectedAccountDetails.value = data.data
    }

    notification.create('success', {
      text: t('pages.marketplace.marketplacePage.success')
    })
  } catch (e: unknown) {
    if (axios.isAxiosError(e) && e.response?.status === 422) {
      notification.create('error', {
        text: t('pages.marketplace.marketplacePage.connectError')
      })
    } else {
      notification.create('error', {
        text: t('pages.marketplace.marketplacePage.globalError')
      })
    }
  }
  isLoadingAccount.value = false
}

const handleAccountConfiguration = async () => {
  if (!selectedPartner.value) return
  isLoadingConfigurationSubmit.value = true
  storeAssociations.value.forEach((s, index) => {
    storeAssociations.value[index].hasError = false
  })
  try {
    const { data } = await axios.put(`/partners/${selectedPartner.value.id}/stores`, {
      partner_stores: storeAssociations.value
        .filter((a) => a.storeIdFromPartner)
        .map((a) => ({
          ...(a.partnerStoreId ? { id: a.partnerStoreId } : {}),
          storeId: a.dropdownSelection.id,
          accountId: selectedAccountConfiguration.value?.id,
          storeIdFromPartner: a.storeIdFromPartner?.toString() || null,
          storeNameFromPartner: a.storeNameFromPartner || null
        }))
    })

    if (data.data.error) {
      let hasError = false
      for (const [key] of Object.entries(data.data.error)) {
        storeAssociations.value[key].hasError = true
        hasError = true
      }
      if (hasError) {
        notification.create('error', {
          text: t('pages.marketplace.marketplacePage.configurationError')
        })
      } else {
        await delay(1000)
        await handleSelectedAccountConfiguration(selectedAccountConfiguration.value)
        notification.create('success', {
          text: t('pages.marketplace.marketplacePage.associationSuccess')
        })
      }
    } else {
      await delay(1000)
      await handleSelectedAccountConfiguration(selectedAccountConfiguration.value)
      notification.create('success', {
        text: t('pages.marketplace.marketplacePage.associationSuccess')
      })
    }
  } catch {
    notification.create('error', {
      text: t('pages.marketplace.marketplacePage.configurationError')
    })
  }

  isLoadingConfigurationSubmit.value = false
}

const resetStoreAssociation = () => {
  storeAssociations.value = stores.value.map((s) => ({
    storeIdFromPartner: null,
    storeNameFromPartner: null,
    partnerStoreId: null,
    hasError: false,
    hash: null,
    dropdownSelection: {
      id: s.id,
      text: s.name
    }
  }))
}

const addAccountStep = () => {
  accountModel.value = {}
  const authType = selectedPartner.value?.partnerAuthenticationType?.slug
  if (
    (['internal', 'time-limited-token'].includes(authType) || !authType) &&
    !selectedPartner.value?.isMerchantAssociationRequired &&
    !selectedPartner.value?.isMonoStoreAssociation
  ) {
    addAccount()
  } else {
    isAuthStep.value = true
  }
}

const resetModal = () => {
  isModalOpen.value = false
  isAuthStep.value = false
  selectedAccountDetails.value = null
  selectedAccountConfiguration.value = null
  accountModel.value = {}
  resetStoreAssociation()
  getVisiblePartners()
}

const configureEmailingPartner = async (partner: EmailingPartner) => {
  selectedEmailingPartner.value = partner
  isEmailingModalOpen.value = true
}

const resetEmailingModal = () => {
  isEmailingModalOpen.value = false
  selectedEmailingPartner.value = null
  emailingFormStep.value = 1
  emailingApiKey.value = ''
  contactLists.value = []
  selectedContactList.value = null
  isLoadingContactLists.value = false
  isActivatingIntegration.value = false
}

// Fonction pour récupérer les listes de contacts (API réelle)
const fetchContactLists = async () => {
  if (!emailingApiKey.value.trim()) {
    notification.create('error', {
      text: t('pages.marketplace.emailingPage.notifications.errors.apiKeyRequired')
    })
    return
  }

  if (!selectedEmailingPartner.value) {
    notification.create('error', {
      text: t('pages.marketplace.emailingPage.notifications.errors.noPartnerSelected')
    })
    return
  }

  isLoadingContactLists.value = true

  try {
    const response = await axios.post('https://automation.heypongo.com/webhook/16a00432-45c9-420e-8ef7-6ebc8cf61d76', {
      emailClient: selectedEmailingPartner.value.slug,
      apiKey: emailingApiKey.value.trim()
    })

    // Transformation des données reçues pour le dropdown
    if (response.data && Array.isArray(response.data)) {
      contactLists.value = response.data.map((list: { id: number; name: string }) => ({
        id: list.id,
        text: list.name
      }))

      if (contactLists.value.length === 0) {
        notification.create('error', {
          text: t('pages.marketplace.emailingPage.notifications.errors.noContactListFound')
        })
      } else {
        emailingFormStep.value = 2
        notification.create('success', {
          text: t('pages.marketplace.emailingPage.notifications.success.contactListsFetched', {
            count: contactLists.value.length
          })
        })
      }
    } else {
      throw new Error('Format de réponse inattendu')
    }
  } catch (error: unknown) {
    console.error('Erreur lors de la récupération des listes:', error)

    if (axios.isAxiosError(error) && error.response?.status === 401) {
      notification.create('error', {
        text: t('pages.marketplace.emailingPage.notifications.errors.invalidApiKey')
      })
    } else if (axios.isAxiosError(error) && error.response?.status === 400) {
      notification.create('error', {
        text: t('pages.marketplace.emailingPage.notifications.errors.invalidParameters')
      })
    } else {
      notification.create('error', {
        text: t('pages.marketplace.emailingPage.notifications.errors.fetchContactListsError')
      })
    }
  } finally {
    isLoadingContactLists.value = false
  }
}

// Fonction pour activer l'intégration (API réelle)
const activateEmailingIntegration = async () => {
  if (!selectedContactList.value?.id) {
    notification.create('error', {
      text: t('pages.marketplace.emailingPage.notifications.errors.contactListRequired')
    })
    return
  }

  if (!selectedEmailingPartner.value) {
    notification.create('error', {
      text: t('pages.marketplace.emailingPage.notifications.errors.noPartnerSelected')
    })
    return
  }

  if (!emailingApiKey.value.trim()) {
    notification.create('error', {
      text: t('pages.marketplace.emailingPage.notifications.errors.apiKeyMissing')
    })
    return
  }

  isActivatingIntegration.value = true

  try {
    // Appel API pour créer l'intégration
    const response = await axios.post('https://automation.heypongo.com/webhook/fe32300d-ef78-4720-8c22-2b1dacd851de', {
      emailClient: selectedEmailingPartner.value.slug,
      apiKey: emailingApiKey.value.trim(),
      listId: selectedContactList.value.id,
      merchantId: user.value.merchant.id,
      merchantName: user.value.merchant.name
    })

    // Vérification de la réponse
    if (response.status === 200) {
      notification.create('success', {
        text: t('pages.marketplace.emailingPage.notifications.success.integrationActivated', {
          partnerName: selectedEmailingPartner.value.name
        })
      })

      // Rafraîchir les intégrations pour mettre à jour les badges
      await fetchEmailingIntegrations()

      // Passer à l'étape de succès au lieu de fermer la modal
      emailingFormStep.value = 3
    } else {
      throw new Error(`Réponse inattendue: ${response.status}`)
    }
  } catch (error: unknown) {
    console.error("Erreur lors de l'activation de l'intégration:", error)

    if (axios.isAxiosError(error) && error.response?.status === 400) {
      notification.create('error', {
        text: t('pages.marketplace.emailingPage.notifications.errors.invalidData')
      })
    } else if (axios.isAxiosError(error) && error.response?.status === 401) {
      notification.create('error', {
        text: t('pages.marketplace.emailingPage.notifications.errors.invalidApiKey')
      })
    } else if (axios.isAxiosError(error) && error.response?.status === 409) {
      notification.create('error', {
        text: t('pages.marketplace.emailingPage.notifications.errors.integrationExists')
      })
    } else {
      notification.create('error', {
        text: t('pages.marketplace.emailingPage.notifications.errors.activationError')
      })
    }
  } finally {
    isActivatingIntegration.value = false
  }
}

// Fonction pour récupérer l'état des intégrations emailing
const fetchEmailingIntegrations = async () => {
  isLoadingEmailingIntegrations.value = true
  try {
    const merchantId = user.value.merchant.id
    const response = await axios.get('https://automation.heypongo.com/webhook/305aee69-2d36-470e-b47e-46778469c71e', {
      params: { merchantId }
    })
    emailingIntegrations.value = response.data || []
  } catch (error) {
    console.error('Erreur lors de la récupération des intégrations emailing:', error)
    emailingIntegrations.value = []
  } finally {
    isLoadingEmailingIntegrations.value = false
  }
}

// Fonction pour activer/désactiver une intégration
const toggleEmailingIntegration = async (partnerSlug: string, newStatus: boolean) => {
  const integration = emailingIntegrations.value.find((i) => i.client === partnerSlug)

  if (!integration?.id) {
    notification.create('error', {
      text: t('pages.marketplace.emailingPage.notifications.errors.integrationIdNotFound')
    })
    return
  }

  isTogglingIntegration.value = integration.id

  try {
    await axios.patch('https://automation.heypongo.com/webhook/9361d2dc-2ab5-4e8d-ac0c-101ab7b3f2e9', {
      id: integration.id,
      active: newStatus
    })

    // Rafraîchir les données après la modification
    await fetchEmailingIntegrations()

    // Chercher le nom du partenaire pour l'affichage
    const partner = emailingPartnersData.value.find((p) => p.slug === partnerSlug)
    const partnerName = partner?.name || partnerSlug

    notification.create('success', {
      text: t('pages.marketplace.emailingPage.notifications.success.integrationToggled', {
        partnerName,
        status: newStatus ? 'activée' : 'désactivée'
      })
    })
  } catch (error) {
    console.error("Erreur lors de la modification de l'intégration:", error)
    notification.create('error', {
      text: t('pages.marketplace.emailingPage.notifications.errors.toggleError')
    })
  } finally {
    isTogglingIntegration.value = null
  }
}

// Fonction pour obtenir l'état d'un partenaire emailing
const getEmailingPartnerStatus = (partnerSlug: string) => {
  const integration = emailingIntegrations.value.find((i) => i.client === partnerSlug)

  if (!integration) {
    // Aucune intégration créée - ne pas afficher de badge
    return { status: 'none', text: '', integration: null }
  }

  if (integration.active) {
    // Intégration active
    return { status: 'active', text: 'pages.marketplace.emailingPage.status.active', integration }
  } else {
    // Intégration créée mais inactive
    return { status: 'inactive', text: 'pages.marketplace.emailingPage.status.inactive', integration }
  }
}

// Fonction pour récupérer les partenaires emailing depuis l'API
const fetchEmailingPartners = async () => {
  isLoadingEmailingPartners.value = true
  try {
    const response = await axios.get('https://automation.heypongo.com/webhook/bf3d6ef2-ac29-438f-90fe-3612c3b8d86a')

    // Filtrer uniquement les partenaires disponibles
    const availablePartners = response.data.filter((partner: EmailingPartner) => partner.available === true)
    emailingPartnersData.value = availablePartners
  } catch (error) {
    console.error('Erreur lors de la récupération des partenaires emailing:', error)
    emailingPartnersData.value = []
    notification.create('error', {
      text: t('pages.marketplace.emailingPage.notifications.errors.loadPartnersError')
    })
  } finally {
    isLoadingEmailingPartners.value = false
  }
}

const goToStoreAssociationStep = () => {
  isAuthStep.value = false
  handleSelectedAccountConfiguration(selectedAccountDetails.value)
  selectedAccountDetails.value = null
}

onMounted(async () => {
  await getVisiblePartners()

  // Charger les données emailing seulement pour les connexions support
  if (isSupportConnection.value) {
    await fetchEmailingIntegrations()
    await fetchEmailingPartners()
  }
})
</script>

<template>
  <div v-if="isLoaded">
    <div>
      <TheListHeader :title="t('pages.marketplace.marketplacePage.title')" />
      <div class="px-6 pb-6 sm:px-12 sm:pb-12">
        <!-- Onglets -->
        <div v-if="isSupportConnection" class="mb-8">
          <BaseTab v-model="activeTab" :tabs="tabs" class="mb-4 sm:mb-0" />
        </div>

        <!-- Contenu des onglets -->
        <!-- Onglet Commandes & paiements -->
        <div v-if="!isSupportConnection || activeTab === 'orders-payments'">
          <div class="grid grid-cols-3 gap-6">
            <Card
              v-for="partner in partners"
              :key="partner.id"
              class="col-span-6 gap-0 p-3.5 md:col-span-6 lg:col-span-3 xl:col-span-1"
            >
              <CardHeader class="flex flex-row items-center justify-between p-0">
                <BaseChip
                  v-if="!partner.isAvailableInMarketplace && !partner.isUsingOpenApi"
                  color="yellow"
                  :text="t('pages.marketplace.marketplacePage.availableSoon')"
                />
                <BaseChip
                  v-if="!partner.isUsingOpenApi && partner.accountCount > 0"
                  color="green"
                  :text="t('pages.marketplace.marketplacePage.connectedAccounts', partner.accountCount)"
                />
                <BaseChip
                  v-if="partner.isUsingOpenApi && partner.accessTokenCount > 0"
                  color="green"
                  :text="t('pages.marketplace.marketplacePage.accessToken', partner.accessTokenCount)"
                />
                <Button
                  v-if="partner.isAvailableInMarketplace && !partner.isUsingOpenApi"
                  as="a"
                  variant="ghost-primary"
                  size="sm"
                  href="#"
                  class="ml-auto"
                  @click="() => openInNewTab(howToUrl(partner.slug))"
                >
                  {{ t('pages.marketplace.marketplacePage.buttons.howTo') }}
                  <ExternalLink />
                </Button>
              </CardHeader>
              <CardContent class="flex flex-col items-center justify-center space-y-2 p-0 py-8">
                <div
                  class="flex h-16 w-16 items-center justify-center rounded-full border-2 border-primary bg-primary/5 p-3"
                >
                  <img :src="partner.logo" :alt="`Logo ${partner.name}`" class="overflow-hidden" />
                </div>
                <h3 class="text-base font-semibold text-foreground">
                  {{ partner.name }}
                </h3>
              </CardContent>
              <CardFooter class="justify-center p-0">
                <Button
                  v-if="
                    (partner.isAvailableInMarketplace && hasPermissionBySlug('marketplace.create')) ||
                    partner.isUsingOpenApi
                  "
                  variant="default-primary"
                  :disabled="isAccountsLoading !== null"
                  @click="() => configure(partner)"
                >
                  <Loader2 v-if="isAccountsLoading === partner.id" class="h-4 w-4 animate-spin" />
                  {{ t('pages.marketplace.marketplacePage.buttons.settings') }}
                </Button>
              </CardFooter>
            </Card>
          </div>
        </div>

        <!-- Onglet Emailing -->
        <div v-else-if="isSupportConnection && activeTab === 'emailing'">
          <!-- Loader pendant le chargement des partenaires ou des intégrations -->
          <div
            v-if="isLoadingEmailingPartners || isLoadingEmailingIntegrations"
            class="flex flex-col items-center justify-center py-12 space-y-4"
          >
            <Spinner class="mb-4 size-8" />
            <p class="text-sm text-foreground">
              {{
                isLoadingEmailingPartners
                  ? t('pages.marketplace.emailingPage.loading.partners')
                  : t('pages.marketplace.emailingPage.loading.integrations')
              }}
            </p>
          </div>
          <!-- Contenu normal -->
          <div v-else class="grid grid-cols-3 gap-6">
            <Card
              v-for="partner in emailingPartnersData"
              :key="partner.name"
              class="col-span-6 gap-0 p-3.5 md:col-span-6 lg:col-span-3 xl:col-span-1"
            >
              <CardHeader class="flex flex-row items-center justify-between p-0">
                <BaseChip
                  v-if="getEmailingPartnerStatus(partner.slug).status === 'active'"
                  color="green"
                  :text="t(getEmailingPartnerStatus(partner.slug).text)"
                />
                <BaseChip
                  v-else-if="getEmailingPartnerStatus(partner.slug).status === 'inactive'"
                  color="red"
                  :text="t(getEmailingPartnerStatus(partner.slug).text)"
                />
                <div v-else></div>
                <Button
                  as="a"
                  variant="ghost-primary"
                  size="sm"
                  href="#"
                  class="ml-auto"
                  @click="() => openInNewTab(partner.howToUrl)"
                >
                  {{ t('pages.marketplace.marketplacePage.buttons.howTo') }}
                  <ExternalLink />
                </Button>
              </CardHeader>
              <CardContent class="flex flex-col items-center justify-center space-y-2 p-0 py-8">
                <div
                  class="flex h-16 w-16 items-center justify-center rounded-full border-2 border-primary bg-primary/5 p-3"
                >
                  <img :src="partner.logo" :alt="`Logo ${partner.name}`" class="h-10 w-10 object-contain" />
                </div>
                <h3 class="text-base font-semibold text-foreground">
                  {{ partner.name }}
                </h3>
              </CardContent>
              <CardFooter class="justify-center p-0">
                <template v-if="getEmailingPartnerStatus(partner.slug).status !== 'none'">
                  <Button
                    v-if="getEmailingPartnerStatus(partner.slug).status === 'inactive'"
                    variant="default-primary"
                    :disabled="isTogglingIntegration !== null || isLoadingEmailingIntegrations"
                    @click="() => toggleEmailingIntegration(partner.slug, true)"
                  >
                    <Loader2
                      v-if="isTogglingIntegration === getEmailingPartnerStatus(partner.slug).integration?.id"
                      class="h-4 w-4 animate-spin"
                    />
                    {{ t('pages.marketplace.emailingPage.buttons.activate') }}
                  </Button>
                  <Button
                    v-else-if="getEmailingPartnerStatus(partner.slug).status === 'active'"
                    variant="outline"
                    :disabled="isTogglingIntegration !== null || isLoadingEmailingIntegrations"
                    @click="() => toggleEmailingIntegration(partner.slug, false)"
                  >
                    <Loader2
                      v-if="isTogglingIntegration === getEmailingPartnerStatus(partner.slug).integration?.id"
                      class="h-4 w-4 animate-spin"
                    />
                    {{ t('pages.marketplace.emailingPage.buttons.deactivate') }}
                  </Button>
                </template>
                <template v-else>
                  <Button
                    variant="default-primary"
                    :disabled="isLoadingEmailingIntegrations"
                    @click="() => configureEmailingPartner(partner)"
                  >
                    {{ t('pages.marketplace.marketplacePage.buttons.settings') }}
                  </Button>
                </template>
              </CardFooter>
            </Card>
          </div>
        </div>
      </div>
    </div>
  </div>
  <TheLoadingPage v-else />
  <BaseModal size="4xl" :is-open="isModalOpen" @close="resetModal">
    <template #customHeadingTitle>
      <div class="flex w-full items-center justify-between">
        <div class="flex items-center">
          <div class="flex h-16 w-16 items-center justify-center rounded-full border-2 border-primary bg-primary/5 p-3">
            <img :src="selectedPartner?.logo" :alt="selectedPartner?.name" class="overflow-hidden" />
          </div>
          <h3 class="ml-4 text-lg font-semibold capitalize">
            {{ selectedPartner?.name }}
          </h3>
        </div>
        <div class="flex items-center gap-3">
          <Button
            v-if="!selectedPartner?.isUsingOpenApi"
            variant="ghost-primary"
            @click="() => openInNewTab(howToUrl(selectedPartner?.slug))"
          >
            {{ t('pages.marketplace.marketplacePage.buttons.howTo') }}
            <ExternalLink />
          </Button>
          <Button
            v-if="selectedPartner?.accountCount && selectedPartner?.accountCount > 0 && isSupportConnection"
            variant="ghost-primary"
            @click="() => selectedPartner && openInNewTab(statusUrl(selectedPartner.id, user?.merchant?.id))"
          >
            {{ t('pages.marketplace.marketplacePage.buttons.status') }}
            <ExternalLink />
          </Button>
        </div>
      </div>
    </template>
    <section class="flex h-full w-full flex-col">
      <div v-if="accounts.length === 0 && !isAuthStep && !selectedPartner?.isUsingOpenApi" class="my-36 text-center">
        <BaseIcon name="HeroRectangleStackIconOutline" class="mx-auto h-12 w-12 text-muted-foreground" />
        <h2 class="mt-2 text-base font-semibold leading-6 text-foreground">
          {{ t('pages.marketplace.marketplacePage.noAccount') }}
        </h2>
        <p class="mb-4 mt-1 text-sm text-muted-foreground">
          {{ t('pages.marketplace.marketplacePage.noAccountDetail', { name: selectedPartner?.name }) }}
        </p>
        <div class="flex items-center justify-center">
          <Button variant="ghost-primary" @click="() => addAccountStep()">
            <Plus />
            {{ t('pages.marketplace.marketplacePage.settings.addAccount') }}
          </Button>
        </div>
      </div>
      <template v-if="isAuthStep">
        <h2 class="mb-2 text-left text-foreground">
          {{ t('pages.marketplace.marketplacePage.addAccount') }}
        </h2>
        <div class="my-6 h-[375px] overflow-y-auto">
          <PartnerAuthForm
            v-model="accountModel"
            :auth-type="selectedPartner.partnerAuthenticationType?.slug"
            :partner="selectedPartner"
          />
        </div>
        <div class="mt-auto flex flex-row items-center justify-between gap-x-2">
          <Button variant="ghost-primary" @click="() => (isAuthStep = false)">
            <ChevronLeft />
            {{ t('pages.marketplace.marketplacePage.back') }}
          </Button>
          <div class="flex gap-x-2">
            <Button
              v-if="selectedPartner?.partnerAuthenticationType?.slug !== 'oauth'"
              variant="default-primary"
              :disabled="isLoadingAccount"
              @click="() => addAccount()"
            >
              <Loader2 v-if="isLoadingAccount" class="h-4 w-4 animate-spin" />
              {{ t('pages.marketplace.marketplacePage.addAccountBtn') }}
            </Button>
          </div>
        </div>
      </template>
      <template v-else-if="selectedAccountDetails">
        <h2 class="mb-2 text-left text-foreground">
          {{ t('pages.marketplace.marketplacePage.accountDetails', { account: selectedAccountDetails?.publicId }) }}
        </h2>
        <div class="my-6 h-[375px] overflow-y-auto text-left text-sm">
          <MarketplaceAccountDetails
            :account="selectedAccountDetails"
            :partner="selectedPartner"
            :is-processing="isSyncingCustomers"
            @handle-sync-requested="(account) => handleCustomerSyncRequested(account)"
          />
        </div>
        <div class="mt-auto flex flex-row items-center justify-between gap-x-2">
          <Button variant="ghost-primary" @click="() => (selectedAccountDetails = null)">
            <ChevronLeft />
            {{ t('pages.marketplace.marketplacePage.back') }}
          </Button>
          <div v-if="selectedPartner?.partnerAuthenticationType?.slug === 'internal'">
            <Button variant="default-primary" @click="goToStoreAssociationStep()">
              {{ t('pages.marketplace.marketplacePage.associate') }}
            </Button>
          </div>
        </div>
      </template>
      <template v-else-if="selectedAccountConfiguration">
        <h2 class="mb-6 text-left text-foreground">
          {{ t('pages.marketplace.marketplacePage.accountConfiguration') }} {{ selectedAccountConfiguration.publicId }}
        </h2>
        <MarketplaceAccountConfiguration
          v-model="storeAssociations"
          :partner="selectedPartner"
          :account="selectedAccountConfiguration"
          :partner-stores="partnerStores"
          :is-loading="isLoadingConfiguration"
          @on-refresh="() => handleSelectedAccountConfiguration(selectedAccountConfiguration)"
        />
        <div class="mt-auto flex flex-row items-center justify-between pt-6">
          <Button variant="ghost-primary" @click="() => (selectedAccountConfiguration = null)">
            <ChevronLeft />
            {{ t('pages.marketplace.marketplacePage.back') }}
          </Button>
          <Button
            v-if="!selectedPartner?.usePartnerStoreHash"
            variant="default-primary"
            :disabled="isLoadingConfigurationSubmit"
            @click="() => handleAccountConfiguration()"
          >
            <Loader2 v-if="isLoadingConfigurationSubmit" class="h-4 w-4 animate-spin" />
            {{ t('pages.marketplace.marketplacePage.confirm') }}
          </Button>
        </div>
      </template>
      <template v-else-if="accounts.length > 0">
        <h2 class="mb-6 text-left text-foreground">
          {{ t('pages.marketplace.marketplacePage.accounts') }}
        </h2>
        <AppSkeleton v-if="isLoadingAccount" type="section" class="h-[375px]" />
        <MarketplaceAccountList
          v-else
          class="mb-6"
          :accounts="accounts"
          :partner="selectedPartner"
          @on-status-update="(account) => handleStatusUpdate(account)"
          @on-selected-account-details="(account) => (selectedAccountDetails = account)"
          @on-selected-account-configuration="(account) => handleSelectedAccountConfiguration(account)"
        />
        <div v-if="!selectedPartner?.isUsingOpenApi" class="mt-auto flex items-center justify-start">
          <Button variant="ghost-primary" :disabled="isLoadingAccount" @click="() => addAccountStep()">
            <Loader2 v-if="isLoadingAccount" class="h-4 w-4 animate-spin" />
            <Plus v-else />
            {{ t('pages.marketplace.marketplacePage.addAccountBtn') }}
          </Button>
        </div>
      </template>
      <template v-if="accounts?.length === 0 && selectedPartner?.isUsingOpenApi">
        <div className="h-[375px] flex items-center flex-col justify-center text-center">
          <BaseIcon name="HeroRectangleStackIconOutline" class="mx-auto h-12 w-12 text-muted-foreground" />
          <h2 class="mt-2 text-base font-semibold leading-6 text-foreground">
            {{ t('pages.marketplace.marketplacePage.noDetailOpenApi') }}
          </h2>
          <p class="mb-4 mt-1 text-sm text-muted-foreground">
            {{ t('pages.marketplace.marketplacePage.buttons.isUsingOpenApi', { name: selectedPartner?.name }) }}
          </p>
        </div>
      </template>
    </section>
  </BaseModal>
  <BaseModal size="4xl" :is-open="isEmailingModalOpen" @close="resetEmailingModal">
    <template #customHeadingTitle>
      <div class="flex w-full items-center justify-between">
        <div class="flex items-center">
          <div class="flex h-16 w-16 items-center justify-center rounded-full border-2 border-primary bg-primary/5 p-3">
            <img
              :src="selectedEmailingPartner?.logo"
              :alt="selectedEmailingPartner?.name"
              class="h-10 w-10 object-contain"
            />
          </div>
          <h3 class="ml-4 text-lg font-semibold capitalize">
            {{ selectedEmailingPartner?.name }}
          </h3>
        </div>

        <!-- Multi-stepper -->
        <div class="flex items-center gap-3">
          <div v-for="(step, index) in emailingSteps" :key="index" class="flex items-center gap-3">
            <div class="flex flex-row items-center gap-3">
              <div
                :class="[
                  'flex h-6 w-6 items-center justify-center rounded-full text-xs transition',
                  emailingFormStep >= index + 1 ? 'bg-primary text-white' : 'bg-primary/5 text-primary'
                ]"
              >
                {{ index + 1 }}
              </div>
              <span
                :class="[
                  'text-xs font-medium',
                  emailingFormStep >= index + 1 ? 'text-primary' : 'text-muted-foreground'
                ]"
              >
                {{ step }}
              </span>
            </div>
            <div
              v-if="index !== emailingSteps.length - 1"
              :class="[
                'w-8 border-y transition',
                emailingFormStep > index + 1 ? 'border-y-primary' : 'border-y-border'
              ]"
            ></div>
          </div>
        </div>
      </div>
    </template>
    <section class="flex h-full w-full flex-col">
      <!-- Étape 1: Saisie de la clé API -->
      <div v-if="emailingFormStep === 1" class="flex flex-col space-y-6">
        <div class="border-b pb-4">
          <h2 class="text-lg font-medium text-foreground">
            {{ t('pages.marketplace.emailingPage.modal.step1.title') }}
          </h2>
          <p class="text-sm text-muted-foreground">
            {{
              t('pages.marketplace.emailingPage.modal.step1.description', {
                partnerName: selectedEmailingPartner?.name
              })
            }}
          </p>
        </div>

        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-foreground mb-2">
              {{
                t('pages.marketplace.emailingPage.modal.step1.apiKeyLabel', {
                  partnerName: selectedEmailingPartner?.name
                })
              }}
            </label>
            <AppFormInput
              id="emailing-api-key"
              v-model="emailingApiKey"
              type="password"
              :placeholder="t('pages.marketplace.emailingPage.modal.step1.apiKeyPlaceholder')"
              :disabled="isLoadingContactLists"
            />
            <p class="text-xs text-muted-foreground mt-1">
              {{
                t('pages.marketplace.emailingPage.modal.step1.apiKeyHelp', {
                  partnerName: selectedEmailingPartner?.name
                })
              }}
              <a
                v-if="selectedEmailingPartner?.findApiKeyUrl"
                :href="selectedEmailingPartner.findApiKeyUrl"
                target="_blank"
                class="text-primary hover:text-primary/80 underline ml-1"
              >
                {{ t('pages.marketplace.emailingPage.modal.step1.officialGuideLink') }}
              </a>
            </p>
          </div>
        </div>

        <div class="flex justify-end space-x-3 pt-4 border-t">
          <Button variant="ghost-primary" :disabled="isLoadingContactLists" @click="resetEmailingModal">
            {{ t('pages.marketplace.emailingPage.modal.step1.buttons.cancel') }}
          </Button>
          <Button
            variant="default-primary"
            :disabled="isLoadingContactLists || !emailingApiKey.trim()"
            @click="fetchContactLists"
          >
            <Loader2 v-if="isLoadingContactLists" class="h-4 w-4 animate-spin" />
            {{ t('pages.marketplace.emailingPage.modal.step1.buttons.fetchLists') }}
          </Button>
        </div>
      </div>

      <!-- Étape 2: Sélection de la liste de contacts -->
      <div v-if="emailingFormStep === 2" class="flex flex-col space-y-6">
        <div class="border-b pb-4">
          <h2 class="text-lg font-semibold text-foreground">
            {{ t('pages.marketplace.emailingPage.modal.step2.title') }}
          </h2>
          <p class="text-sm text-muted-foreground">
            {{ t('pages.marketplace.emailingPage.modal.step2.description') }}
          </p>
        </div>

        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-foreground mb-2">{{
              t('pages.marketplace.emailingPage.modal.step2.contactListLabel')
            }}</label>
            <ComboboxSelect
              value-key="id"
              label-key="text"
              :options="contactLists"
              :model-value="selectedContactList?.id"
              :placeholder="t('pages.marketplace.emailingPage.modal.step2.contactListPlaceholder')"
              :disabled="isActivatingIntegration"
              @update:model-value="
                (id: number) => (selectedContactList = contactLists.find((list) => list.id === id) ?? null)
              "
            />
            <p class="text-xs text-muted-foreground mt-1">
              {{ t('pages.marketplace.emailingPage.modal.step2.contactListHelp') }}
            </p>
          </div>

          <!-- Informations sur l'intégration -->
          <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div class="flex items-start">
              <BaseIcon name="HeroInformationCircleIconOutline" class="h-5 w-5 text-blue-500 mr-2" />
              <div>
                <h4 class="text-sm font-medium text-blue-800">
                  {{ t('pages.marketplace.emailingPage.modal.step2.importantInfo.title') }}
                </h4>
                <p class="text-sm text-blue-700 mt-1">
                  {{ t('pages.marketplace.emailingPage.modal.step2.importantInfo.description') }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div class="flex justify-between space-x-3 pt-4 border-t">
          <Button variant="ghost-primary" :disabled="isActivatingIntegration" @click="emailingFormStep = 1">
            <ChevronLeft />
            {{ t('pages.marketplace.emailingPage.modal.step2.buttons.back') }}
          </Button>
          <Button
            variant="default-primary"
            :disabled="isActivatingIntegration || !selectedContactList?.id"
            @click="activateEmailingIntegration"
          >
            <Loader2 v-if="isActivatingIntegration" class="h-4 w-4 animate-spin" />
            {{ t('pages.marketplace.emailingPage.modal.step2.buttons.activate') }}
          </Button>
        </div>
      </div>

      <!-- Étape 3: Succès -->
      <div v-if="emailingFormStep === 3" class="flex flex-col space-y-6">
        <div class="text-center">
          <BaseIcon name="HeroCheckCircleIconSolid" class="mx-auto h-16 w-16 text-green-500 mb-4" />
          <h2 class="text-xl font-semibold text-foreground mb-2">
            {{ t('pages.marketplace.emailingPage.modal.step3.title') }}
          </h2>
          <p class="text-foreground-secondary mb-6">
            {{ t('pages.marketplace.emailingPage.modal.step3.subtitle', { listName: selectedContactList?.text }) }}
          </p>
        </div>

        <div class="space-y-6">
          <!-- Import des contacts existants -->
          <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div class="flex items-start">
              <BaseIcon
                name="HeroQuestionMarkCircleIconOutline"
                class="h-5 w-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0"
              />
              <div class="flex-1">
                <h4 class="text-sm font-medium text-blue-800">
                  {{ t('pages.marketplace.emailingPage.modal.step3.existingContacts.title') }}
                </h4>
                <p class="text-sm text-blue-700 mt-1 mb-3">
                  {{ t('pages.marketplace.emailingPage.modal.step3.existingContacts.description') }}
                </p>
                <Button variant="outline" size="sm" @click="() => openInNewTab('/customer')">
                  {{ t('pages.marketplace.emailingPage.modal.step3.existingContacts.button') }}
                  <ExternalLink />
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div class="flex justify-center pt-4 border-t">
          <Button variant="default-primary" @click="resetEmailingModal">
            {{ t('pages.marketplace.emailingPage.modal.step3.buttons.close') }}
          </Button>
        </div>
      </div>
    </section>
  </BaseModal>
</template>
